/**
 * The on-screen controls (docs/design/mobile-layout.md §7; `q-touch-controls-overlay`, T3 of
 * AD-817): a left-thumb movement stick with a dynamic origin and a right-thumb cluster of the
 * ability slots 1–6 + Dodge — combat.md's seven-button ceiling. Rendered by the HUD only when
 * the player has switched Settings → Display → On-screen controls on
 * (`useMobileControlsEnabled()`); off, nothing here exists.
 *
 * The rules live in pure modules: `game/touch/virtualStick.ts` (dead zone, clamp, origin),
 * `ui/touch/touchRouting.ts` (one finger per control, the bar's buttons). This file is the
 * thin DOM binding. Every control is a DOM element above the canvas, so a touch that starts
 * on it never reaches T1's canvas tap/orbit/pinch handlers (§7.5).
 */

import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { requestDodge } from '../../game/input.js';
import { stickChannel } from '../../game/touch/stickChannel.js';
import { VirtualStick, type Point } from '../../game/touch/virtualStick.js';
import { AbilityIcon } from '../AbilityIcon.js';
import { HudIcon } from '../HudIcon.js';
import { AbilitySlotFill, slotStateClass } from '../AbilitySlotFill.js';
import type { AbilitySlotView } from '../abilitySlotState.js';
import { humanizeId } from '../playerText.js';
import { TouchRouter, touchBarButtons, type TouchControl } from './touchRouting.js';

export interface TouchControlsOverlayProps {
  /** The loadout's abilities in key order (slot 1 = key `1`). */
  abilities: readonly { id: string; kind: string }[];
  /** The ONE ability press the keys 1–6 use (`sendAbilityPress`). */
  onAbility: (abilityId: string) => void;
  /** Dodge readiness 0..1 — the same number the vitals' dodge bar draws. */
  dodgeReadyPct: number;
  /** Each slot's cooldown fill + stamina dim (AD-849), keyed by ability id; absent ⇒ ready. */
  slotViews?: Readonly<Record<string, AbilitySlotView>>;
}

interface KnobView {
  origin: Point;
  knobX: number;
  knobY: number;
}

export function TouchControlsOverlay({
  abilities,
  onAbility,
  dodgeReadyPct,
  slotViews = {},
}: TouchControlsOverlayProps): JSX.Element {
  const router = useRef(new TouchRouter()).current;
  const stick = useRef(new VirtualStick()).current;
  const zoneRect = useRef<DOMRect | null>(null);
  const [knob, setKnob] = useState<KnobView | null>(null);

  // Unmount (setting switched off, leaving the world) or a lost window: nothing stays held.
  useEffect(() => {
    const drop = (): void => {
      router.reset();
      stick.reset();
      stickChannel.clear();
      setKnob(null);
    };
    window.addEventListener('blur', drop);
    return () => {
      window.removeEventListener('blur', drop);
      drop();
    };
  }, [router, stick]);

  const local = (e: ReactPointerEvent<HTMLElement>): Point => {
    const r = zoneRect.current;
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  const showStick = (): void => {
    const reading = stick.reading;
    stickChannel.set(reading.ix, reading.iz);
    setKnob(
      stick.owner === null
        ? null
        : { origin: stick.origin, knobX: reading.knobX, knobY: reading.knobY },
    );
  };

  const capture = (e: ReactPointerEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // A pointer that already ended cannot be captured; the press still counts.
    }
  };

  const onZoneDown = (e: ReactPointerEvent<HTMLElement>): void => {
    if (!router.claim(e.pointerId, 'stick')) return;
    capture(e);
    const rect = e.currentTarget.getBoundingClientRect();
    zoneRect.current = rect;
    stick.down(e.pointerId, local(e), { left: 0, top: 0, width: rect.width, height: rect.height });
    showStick();
  };
  const onZoneMove = (e: ReactPointerEvent<HTMLElement>): void => {
    if (router.controlOf(e.pointerId) !== 'stick') return;
    stick.move(e.pointerId, local(e));
    showStick();
  };
  const onZoneUp = (e: ReactPointerEvent<HTMLElement>): void => {
    if (router.controlOf(e.pointerId) !== 'stick') return;
    router.release(e.pointerId);
    stick.up(e.pointerId);
    showStick();
  };

  const onButtonDown = (control: TouchControl, act: () => void) => {
    return (e: ReactPointerEvent<HTMLElement>): void => {
      if (!router.claim(e.pointerId, control)) return;
      capture(e);
      act();
    };
  };
  // A pointer press acts on pointerdown; a click with `detail === 0` is a keyboard activation
  // (Enter/Space on a focused button) — acted on too, so the bar stays operable without touch.
  const onKeyboardActivate = (act: () => void) => {
    return (e: { detail: number }): void => {
      if (e.detail === 0) act();
    };
  };
  const onButtonUp = (e: ReactPointerEvent<HTMLElement>): void => {
    router.release(e.pointerId);
  };

  const ready = Math.max(0, Math.min(1, dodgeReadyPct));
  return (
    <div className="touch-overlay" data-testid="touch-overlay">
      <div
        className="touch-stick-zone interactive"
        data-testid="touch-stick-zone"
        onPointerDown={onZoneDown}
        onPointerMove={onZoneMove}
        onPointerUp={onZoneUp}
        onPointerCancel={onZoneUp}
        onLostPointerCapture={onZoneUp}
      >
        {knob ? (
          <div
            className="touch-stick-ring"
            data-testid="touch-stick-ring"
            style={{ left: knob.origin.x, top: knob.origin.y }}
          >
            <div
              className="touch-stick-knob"
              style={{ transform: `translate(${knob.knobX}px, ${knob.knobY}px)` }}
            />
          </div>
        ) : null}
      </div>
      <div className="touch-bar" data-testid="touch-bar">
        {touchBarButtons(abilities).map((b) =>
          b.abilityId !== null && b.slot !== null ? (
            <button
              key={b.control}
              type="button"
              className={`touch-btn touch-slot interactive${slotStateClass(slotViews[b.abilityId])}`}
              style={{ gridArea: `s${b.slot}` }}
              data-testid={`touch-ability-${b.slot}`}
              aria-label={`ability ${b.slot}: ${humanizeId(b.abilityId)}`}
              data-cd={slotViews[b.abilityId]?.state ?? 'ready'}
              onPointerDown={onButtonDown(b.control, () => onAbility(b.abilityId as string))}
              onClick={onKeyboardActivate(() => onAbility(b.abilityId as string))}
              onPointerUp={onButtonUp}
              onPointerCancel={onButtonUp}
              onLostPointerCapture={onButtonUp}
            >
              <span className="key">{b.slot}</span>
              <AbilityIcon abilityId={b.abilityId} label={humanizeId(b.abilityId)} size={30} />
              <AbilitySlotFill view={slotViews[b.abilityId]} />
            </button>
          ) : (
            <button
              key={b.control}
              type="button"
              className="touch-btn touch-dodge interactive"
              style={{ gridArea: 'dodge' }}
              data-testid="touch-dodge"
              aria-label="dodge"
              onPointerDown={onButtonDown(b.control, requestDodge)}
              onClick={onKeyboardActivate(requestDodge)}
              onPointerUp={onButtonUp}
              onPointerCancel={onButtonUp}
              onLostPointerCapture={onButtonUp}
            >
              <span
                className="touch-dodge-fill"
                style={{ height: `${Math.round(ready * 100)}%` }}
              />
              <HudIcon id="dodge" size={28} />
              <span className="touch-dodge-label hud-caption">Dodge</span>
            </button>
          ),
        )}
      </div>
    </div>
  );
}
